import React, { useEffect, useState } from 'react';
import PageContent from '@/components/PageContent';
import { Col, Divider, Form, Grid, IconButton, NumberInput, Panel, Row, SelectPicker, Stack, FormInstance, Schema } from 'rsuite';
import FetchingSelect from '@/components/FetchingSelect';
import { Achievement, AchievementTypeEnum, AchievementTypeList, CityDistrictAchievementType, DiscoveryPointAchievementType, FirstCityVisitAchievementType, FirstCountryVisitAchievementType, MarathonAchievementType, RouteAchievementType, RunEventAchievementType, SpecificType, UpsertAchievement, UpsertCityDistrictAchievementType, UpsertDiscoveryPointAchievementType, UpsertMarathonAchievementType, UpsertRouteAchievementType, UpsertRunEventAchievementType, UpsertSpecificType } from '@/models/achievement.model';
import PageNextIcon from '@rsuite/icons/PageNext';
import PagePreviousIcon from '@rsuite/icons/PagePrevious';
import { BaseErrorModel, BaseResponseModel, PageState } from '@/models/core.model';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import ErrorMessage from '@/components/ErrorMessage';
import { upsertAchievement } from '@/services/achievement.service';
import ImageUploader from '@/components/ImageUploader';
import { MdEmojiEvents } from 'react-icons/md';
import { ObjectStorageFileCategory } from '@/models/storage.model';
import { getCitiesLookup } from '@/services/city.service';
import { getCountriesLookup } from '@/services/country.service';
import FirstCityVisitForm from './achievement-first-city-visit-form';
import FirstCountryVisitForm from './achievement-first-country-visit-form';
import DiscoveryPointForm, { DiscoveryPointAchievementInitValue } from './achievement-discovery-point-form';
import RouteForm, { RouteAchievementInitValue } from './achievement-route-form';
import MarathonForm, { MarathonAchievementInitValue } from './achievement-marathon-form';
import CityDistrictForm, { CityDistrictAchievementInitValue } from './achievement-city-district-form';
import RunEventForm, { RunEventAchievementInitValue } from './achievement-run-event-form';

const modelSchema = Schema.Model<UpsertAchievement> ({
    id: Schema.Types.NumberType(),
    type: Schema.Types.NumberType().isRequired('Type is required'),
    name: Schema.Types.StringType().isRequired('Name is required'),
    supportingCityId: Schema.Types.NumberType(),
    supportingCountryId: Schema.Types.NumberType(),
    uniqueNumber: Schema.Types.NumberType(),
});

const emptyModel: UpsertAchievement = {
    id: undefined,
    type: AchievementTypeEnum.FirstCityVisit,
    name: '',
    supportingCityId: undefined,
    supportingCountryId: undefined,
    uniqueNumber: undefined
};

type AchievementInitValue = UpsertAchievement & (CityDistrictAchievementInitValue | DiscoveryPointAchievementInitValue | RouteAchievementInitValue | MarathonAchievementInitValue | RunEventAchievementInitValue);

interface MainAchievementFormProps {
    initValue?: AchievementInitValue;
    formObject?: Achievement;
    specificFormObject?: SpecificType;
    pageState: PageState,
}

const MainAchievementForm: React.FC<MainAchievementFormProps> = 
  ({initValue, formObject, specificFormObject, pageState}) => {
    const [formValue, setFormValue] = useState<UpsertAchievement>(emptyModel);
    const [initSpecificFormObject, setInitSpecificFormObject] = useState<SpecificType|undefined>(undefined);
    const [specificFormValue, setSpecificFormValue] = useState<UpsertSpecificType|undefined>(undefined);
    const [formError, setFormError] = useState({});
    const [errorModel, setErrorModel] = useState<BaseErrorModel|undefined>(undefined);
    const [loading, setLoading] = useState(0);

    const nav = useNavigate();
    const formRef = React.useRef<FormInstance<Record<string, any>>>(null);
    const specificFormRef = React.useRef<FormInstance<Record<string, any>>>(null);

    useEffect(() => {
        if(pageState == PageState.New){
          setFormValue({
            ...emptyModel, 
            type: initValue?.type ?? AchievementTypeEnum.FirstCityVisit,
            name: initValue?.name ?? '',
            supportingCityId: initValue?.supportingCityId,
            supportingCountryId: initValue?.supportingCountryId
          });
          return;
        }
        if (formObject) {
          const castedModel: UpsertAchievement = { 
            ...formObject,
          };
          
          setFormValue(castedModel);
          setInitSpecificFormObject(specificFormObject);
        } else {
            setFormValue(emptyModel);
            setInitSpecificFormObject(undefined);
        }
    }, [formObject]);
    
    const handleSubmit = async () => {
        // console.log(formValue);
        // console.log(specificFormValue);
        const mainFormCheck = formRef.current && !formRef.current.check();
        const specificFormCheck = specificFormRef.current && !specificFormRef.current.check();

        if (mainFormCheck || specificFormCheck){
            console.log(formValue, formError);
            return;
        }
        setErrorModel(undefined);
        setLoading(pre=>++pre);
        try {
            const result: BaseResponseModel<null> = await upsertAchievement({...formValue, ...specificFormValue});
            if (result.success) {
                nav('/achievements');
            }
        } catch (err) {
            setErrorModel((err as AxiosError<BaseErrorModel>).response?.data);
        }
        finally{
            setLoading(pre=>--pre);
        }
    };

    const handleSpecificFormValueChange = (newFormValue: SpecificType) => {
        setSpecificFormValue(newFormValue);
    };

    const handleFormChange = (value: Record<string, any>) => {
        setFormValue(prevFormValue => ({
          ...prevFormValue,
          ...value
        }));
      };

      const handleTypeChange = type => {
        setFormValue(pre => ({ ...pre, type}));
        setInitSpecificFormObject(undefined);
      };
    return (
    <>
      <PageContent>
        <Form name='upsert-achievement' fluid 
          ref={formRef}
          onChange={handleFormChange}
          onCheck={setFormError}
          formValue={formValue}
          model={modelSchema}
          disabled={!!loading}
        >
          <Grid w='100%' fluid>
            <Row>
              <Col span={{xs:24, lg:6, xl:4}} style={{textAlign: 'center'}}>
                <Form.Group>
                  <Form.Label>Icon</Form.Label>
                  <Form.Control
                    accepter={ImageUploader}
                    name="iconUrl"
                    defaultIcon={MdEmojiEvents}
                    height="150px"
                    width="150px"
                    url={formValue.iconUrl}
                    objectStorageCategory={ObjectStorageFileCategory.Achievement}
                    onChange={url => setFormValue(pre => ({...pre, iconUrl: url}))}                  
                  />
                </Form.Group>
              </Col>
              <Col span={{xs:24, lg:18, xl:20}} >
                <Form.Group>
                  <Form.Label>Name</Form.Label>
                  <Form.Control name="name" value={formValue?.name ?? ''}/>
                </Form.Group>
                <Form.Group>
                  <Form.Label>Supporting City</Form.Label>
                  <Form.Control name="supportingCityId" accepter={FetchingSelect} block
                    fetchData={(text, page) => getCitiesLookup(text, undefined, page)} 
                    placeholder="Select a city"
                    disabled={!!initValue?.supportingCityId}
                  />                       
                </Form.Group>
                <Form.Group>
                  <Form.Label>Supporting Country</Form.Label>
                  <Form.Control name="supportingCountryId" accepter={FetchingSelect} block
                    fetchData={(text, page) => getCountriesLookup(text, page)} 
                    placeholder="Select a country"
                    disabled={!!initValue?.supportingCountryId}
                  />                       
                </Form.Group>
                <Form.Group>
                  <Form.Label>Unique Number</Form.Label>
                  <Form.Control name="uniqueNumber" accepter={NumberInput} />
                </Form.Group>
              </Col>
            </Row>
            <Divider/>
            <Row>
              <Col span={{xs:24, lg:6}} >
                <Form.Group>
                  <Form.Label>Type</Form.Label>
                  <Form.Control name="type" accepter={SelectPicker} block
                    data={AchievementTypeList} 
                    searchable={false} 
                    onChange={handleTypeChange}
                    cleanable={false}
                    disabled={!!initValue?.type || pageState == PageState.Edit}
                  />
                </Form.Group>
              </Col >
            </Row> 
          </Grid>
        </Form>


        {formValue?.type != undefined && <Panel bordered>
          {formValue.type == AchievementTypeEnum.FirstCityVisit && 
            <FirstCityVisitForm 
                formObject={initSpecificFormObject as FirstCityVisitAchievementType}  
                onFormChange={handleSpecificFormValueChange}  
                formRef={specificFormRef}
                loading={loading}
            />
          }
          {formValue.type == AchievementTypeEnum.MultiCityVisit && <></>}
          {formValue.type == AchievementTypeEnum.FirstCountryVisit && 
            <FirstCountryVisitForm 
                formObject={initSpecificFormObject as FirstCountryVisitAchievementType}  
                onFormChange={handleSpecificFormValueChange}  
                formRef={specificFormRef}
                loading={loading}
            />
          }
          {formValue.type == AchievementTypeEnum.MultiCountryVisit && <></>}
          {formValue.type == AchievementTypeEnum.DiscoveryPoint && 
            <DiscoveryPointForm 
                formObject={initSpecificFormObject as DiscoveryPointAchievementType}  
                onFormChange={handleSpecificFormValueChange}  
                formRef={specificFormRef}
                loading={loading}
                initValue={(initValue as DiscoveryPointAchievementInitValue)}
            />
          }
          {formValue.type == AchievementTypeEnum.Routes && 
            <RouteForm 
                formObject={initSpecificFormObject as RouteAchievementType}  
                onFormChange={handleSpecificFormValueChange}  
                formRef={specificFormRef}
                loading={loading}
                initValue={(initValue as RouteAchievementInitValue)}
            />
          }
          {formValue.type == AchievementTypeEnum.Marathon && 
            <MarathonForm 
                formObject={initSpecificFormObject as MarathonAchievementType}  
                onFormChange={handleSpecificFormValueChange}  
                formRef={specificFormRef}
                loading={loading}
                initValue={(initValue as MarathonAchievementInitValue)}
            />
          }
          {formValue.type == AchievementTypeEnum.Event && 
            <RunEventForm 
                formObject={initSpecificFormObject as RunEventAchievementType}  
                onFormChange={handleSpecificFormValueChange}  
                formRef={specificFormRef}
                loading={loading}
                initValue={(initValue as RunEventAchievementInitValue)}
            />
          }
          {formValue.type == AchievementTypeEnum.District && 
            <CityDistrictForm 
                formObject={initSpecificFormObject as CityDistrictAchievementType}  
                onFormChange={handleSpecificFormValueChange}  
                formRef={specificFormRef}
                loading={loading}
            />
          }
          
        </Panel>
        }
        <Divider />
        {errorModel && <ErrorMessage model={errorModel} style={{ marginTop: 20, marginBottom:20 }}></ErrorMessage>}

        <Stack justifyContent="space-between">
        <IconButton icon={<PagePreviousIcon />} onClick={() => nav(-1)}>
            Back
        </IconButton>
        <IconButton
            icon={<PageNextIcon />}
            placement="right"
            appearance="primary"
            onClick={handleSubmit}
            loading={!!loading}
        >
            Save
        </IconButton>
        </Stack>

      </PageContent>
    </>
    );
};


export default MainAchievementForm;